import React, { useEffect, useMemo, useState } from 'react';
import { WORKSPACE_ACCESS_LABELS_ID } from '../../../config/constants';
import { Box, Typography, Row, Col } from '@lucid/lego';
import styles from './WorkspaceAccess.module.scss';
import Listing from './Listing/Listing';
import Details from './Details/Details';
import { useStore } from '../../../store/store';
import { useGetPartnerUsers } from '../../../api/services/lupp/onboarding/onboarding';
import type { PartnerUser } from '../../../api/services/lupp/onboarding/dto.onboarding';
import { useLabels } from '../../../hooks/useLabels';
import { convertToContentfulLocale } from '../../../utils/tools';

export type User = {
  profileId: string;
  name: string;
  title: string;
  email: string;
  workspaceCount: number;
  workspaces: {
    name: string;
    isActive: boolean;
  }[];
};

/**
 * Workspace Access page component for managing user access to workspaces.
 * Displays a list of partner users and their workspace access details.
 *
 * @component
 * @returns {JSX.Element} Workspace Access page
 *
 * @example
 * <WorkspaceAccess />
 */
const WorkspaceAccess: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const {
    data: labels,
    isLoading: labelsLoading,
    error: labelsError,
  } = useLabels({
    id: WORKSPACE_ACCESS_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  const {
    data: usersData,
    isLoading,
    error,
  } = useGetPartnerUsers({
    page: 0,
    perPage: 100,
    enabled: !labelsLoading && !labelsError,
  });

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
  };

  // Transform PartnerUser to User type
  const transformedUsers: User[] = useMemo(
    () =>
      usersData?.items.map((user: PartnerUser) => ({
        profileId: user.user_id,
        name: user.name,
        title: 'User',
        email: user.email,
        workspaceCount: 0,
        workspaces: [],
      })) || [],
    [usersData?.items]
  );

  useEffect(() => {
    if (transformedUsers.length && !selectedUser) {
      setSelectedUser(transformedUsers[0]);
    }
  }, [transformedUsers, selectedUser]);

  return (
    <Row gutter={[0, 0]} wrap={false}>
      <Col xs={12} md={3} xxl={4} className={styles.fullHeightPanel}>
        <Box pb={32}>
          <Typography theme={theme} variant="headline" size="small" color="primary-1">
            {labels?.title || 'Workspace Access'}
          </Typography>
        </Box>
        <Box pr={16} className={styles.scrollable}>
          {isLoading && (
            <Typography theme={theme} variant="body" size="medium" color="primary-2">
              {labels?.loading || 'Loading users...'}
            </Typography>
          )}
          {error && (
            <Typography theme={theme} variant="body" size="medium" color="error-1">
              {labels?.error || 'Error loading users. Please try again.'}
            </Typography>
          )}
          {!isLoading && !error && (
            <Listing
              data={transformedUsers}
              userDetail={handleUserSelect}
              selectedUserId={selectedUser?.profileId}
            />
          )}
        </Box>
      </Col>
      <Col xs={12} md={9} xxl={8} className={styles.fullHeightPanel}>
        <Details selectedUser={selectedUser} />
      </Col>
    </Row>
  );
};

export default WorkspaceAccess;
